import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function NorthKoreaWomenRightsPost() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <article className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-3 text-sm text-gray-600 mb-4">
              <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full font-semibold">Human Rights Crisis</span>
              <span>•</span>
              <time>January 2025</time>
              <span>•</span>
              <span>18 min read</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              The Invisible Women of North Korea: A Silent Crisis
            </h1>
            
            <p className="text-2xl text-gray-700 leading-relaxed mb-8">
              Behind the world's most closed border, North Korean women face systematic oppression, trafficking, forced labor, and sexual violence—all hidden from international view.
            </p>

            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                <i className="ri-women-line text-xl text-white"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900">SheCan Team</p>
                <p className="text-sm text-gray-600">Human Rights Research</p>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/5458be966728abfddadee416a6f36f4f.jpg"
              alt="North Korea Women's Rights"
              className="w-full h-[500px] object-cover"
            />
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8 rounded-r-xl">
              <p className="text-red-900 font-semibold mb-2">
                <i className="ri-alert-line mr-2"></i>
                Content Warning
              </p>
              <p className="text-red-800 text-sm">
                This article discusses sexual violence, human trafficking, forced labor, and severe human rights abuses.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The World's Most Closed Society</h2>
            
            <p className="text-lg text-gray-800 leading-relaxed mb-6">
              North Korea is often called the "Hermit Kingdom"—a nation so isolated that almost nothing gets in or out. For women living under Kim Jong Un's regime, this isolation means their suffering happens in complete darkness, hidden from the world that could help them.
            </p>

            <p className="text-lg text-gray-800 leading-relaxed mb-6">
              While the international community focuses on nuclear weapons and political tensions, <strong>26 million people live under one of the world's most oppressive regimes</strong>—and half of them are women facing unique and systematic forms of abuse.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Life Inside: What We Know</h2>

            <div className="bg-gray-50 p-8 rounded-2xl mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Daily Reality for North Korean Women:</h3>
              
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-line text-red-600"></i>
                  </div>
                  <div>
                    <strong className="text-gray-900">No Freedom of Movement:</strong>
                    <p className="text-gray-700">Women cannot travel within their own country without government permission. Leaving the country is considered treason, punishable by death or life in a labor camp.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-line text-red-600"></i>
                  </div>
                  <div>
                    <strong className="text-gray-900">No Access to Information:</strong>
                    <p className="text-gray-700">No internet, no foreign media, no outside news. Women have no way to know that their treatment is not normal, or that help exists beyond their borders.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-line text-red-600"></i>
                  </div>
                  <div>
                    <strong className="text-gray-900">Songbun System:</strong>
                    <p className="text-gray-700">A hereditary caste system that determines every aspect of life—where you can live, work, study, and even who you can marry. Women inherit their family's "loyalty" status and cannot escape it.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-line text-red-600"></i>
                  </div>
                  <div>
                    <strong className="text-gray-900">Forced Labor:</strong>
                    <p className="text-gray-700">Women work in factories, farms, and construction sites with no labor protections, no safety standards, and minimal or no pay.</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Sexual Violence as Government Policy</h2>

            <p className="text-lg text-gray-800 leading-relaxed mb-6">
              According to testimonies from North Korean defectors, <strong>sexual violence against women is normalized and systematic</strong> in North Korea:
            </p>

            <ul className="space-y-4 mb-8">
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-pink-600 text-2xl flex-shrink-0"></i>
                <span className="text-gray-800"><strong>Police and military officials</strong> routinely sexually assault women with complete impunity. Women cannot report these crimes because the perpetrators ARE the authorities.</span>
              </li>
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-pink-600 text-2xl flex-shrink-0"></i>
                <span className="text-gray-800"><strong>In detention centers and labor camps</strong>, sexual abuse of female prisoners is widespread and systematic. Guards use rape as punishment and control.</span>
              </li>
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-pink-600 text-2xl flex-shrink-0"></i>
                <span className="text-gray-800"><strong>Market women</strong> (who sell goods to survive) must pay bribes to police—often in the form of sexual favors—to avoid arrest.</span>
              </li>
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-pink-600 text-2xl flex-shrink-0"></i>
                <span className="text-gray-800"><strong>Forced abortions and infanticide</strong> are performed on women who become pregnant in labor camps or by Chinese fathers after trafficking.</span>
              </li>
            </ul>

            <blockquote className="border-l-4 border-pink-600 pl-6 py-4 my-8 bg-pink-50 rounded-r-xl">
              <p className="text-xl text-gray-900 italic mb-2">
                "I thought it was normal. I thought that's just what men in power do to women. It wasn't until I escaped and talked to other women that I realized—this is not normal. This is not how women should live."
              </p>
              <footer className="text-sm text-gray-700">
                — North Korean defector testimony, 2023
              </footer>
            </blockquote>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Trafficking Pipeline</h2>

            <p className="text-lg text-gray-800 leading-relaxed mb-6">
              Desperate to escape famine, violence, and oppression, thousands of North Korean women risk everything to cross the border into China. <strong>But escape often leads to a worse nightmare.</strong>
            </p>

            <div className="bg-yellow-50 p-8 rounded-2xl mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">What Happens at the Chinese Border:</h3>
              
              <ol className="space-y-4 list-decimal list-inside">
                <li className="text-gray-800">
                  <strong>Immediate Trafficking Risk:</strong> Chinese brokers wait at the border to "purchase" North Korean women. They're sold to Chinese men as brides for $1,000-$10,000.
                </li>
                <li className="text-gray-800">
                  <strong>No Legal Status:</strong> China considers North Korean refugees "illegal economic migrants" and deports them back to North Korea, where they face torture, labor camps, or execution.
                </li>
                <li className="text-gray-800">
                  <strong>Forced Marriage & Sexual Slavery:</strong> Women are trapped in rural Chinese villages, unable to speak the language, with no identification papers, no way to escape.
                </li>
                <li className="text-gray-800">
                  <strong>Children Born Stateless:</strong> Children born to North Korean mothers in China have no legal identity and cannot access healthcare or education.
                </li>
              </ol>
            </div>

            <p className="text-lg text-gray-800 leading-relaxed mb-6">
              <strong>An estimated 80-90% of North Korean women who cross into China are trafficked.</strong> They escape one prison only to find themselves in another.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Women Who Made It Out</h2>

            <p className="text-lg text-gray-800 leading-relaxed mb-6">
              Despite unimaginable odds, some North Korean women do escape and survive to tell their stories:
            </p>

            <div className="space-y-6 mb-8">
              <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
                <h4 className="text-xl font-bold text-gray-900 mb-2">Yeonmi Park</h4>
                <p className="text-gray-700 mb-2">
                  Escaped at 13 with her mother, trafficked in China, eventually made it to South Korea. Now a human rights activist speaking globally about North Korean women's suffering.
                </p>
                <p className="text-sm text-pink-600 italic">
                  "In North Korea, we were told that we were the happiest people on Earth. I believed it because I had nothing to compare it to."
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
                <h4 className="text-xl font-bold text-gray-900 mb-2">Jihyun Park</h4>
                <p className="text-gray-700 mb-2">
                  Escaped, was trafficked, got pregnant, returned to North Korea, survived a labor camp while pregnant, escaped again with her son. Now helps other defectors in the UK.
                </p>
                <p className="text-sm text-pink-600 italic">
                  "I lived through hell twice to give my son a life worth living."
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
                <h4 className="text-xl font-bold text-gray-900 mb-2">Hyeonseo Lee</h4>
                <p className="text-gray-700 mb-2">
                  Lived in China for 10 years in hiding, eventually reached South Korea. Risked everything to go back and rescue her mother and brother from North Korea.
                </p>
                <p className="text-sm text-pink-600 italic">
                  "I had tasted freedom. I couldn't leave my family behind in that prison."
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Why The World Stays Silent</h2>

            <p className="text-lg text-gray-800 leading-relaxed mb-6">
              North Korea's nuclear weapons program dominates international attention. Women's rights violations are considered "secondary concerns" in diplomatic negotiations.
            </p>

            <ul className="space-y-4 mb-8">
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-pink-600 text-2xl flex-shrink-0"></i>
                <span className="text-gray-800"><strong>China's complicity:</strong> China refuses to grant refugee status to North Korean women, instead deporting them back to certain torture or death.</span>
              </li>
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-pink-600 text-2xl flex-shrink-0"></i>
                <span className="text-gray-800"><strong>Media blackout:</strong> No foreign journalists are allowed in. All information comes from defectors, making documentation difficult.</span>
              </li>
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-pink-600 text-2xl flex-shrink-0"></i>
                <span className="text-gray-800"><strong>Political priorities:</strong> Western governments prioritize nuclear disarmament over human rights in negotiations.</span>
              </li>
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-pink-600 text-2xl flex-shrink-0"></i>
                <span className="text-gray-800"><strong>Public ignorance:</strong> Most people know North Korea for its missiles and dictator—not for the millions of women suffering in silence.</span>
              </li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What Can Be Done</h2>

            <p className="text-lg text-gray-800 leading-relaxed mb-6">
              We cannot wait for North Korea's regime to change. <strong>We must act now to support the women escaping and amplify the voices of survivors.</strong>
            </p>

            <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-8 rounded-2xl mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">How You Can Help:</h3>
              
              <div className="space-y-4">
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <strong className="text-gray-900 text-lg">Support Organizations Helping Defectors</strong>
                    <p className="text-gray-700">Liberty in North Korea (LiNK), NKDB, Teach North Korean Refugees (TNKR), and others provide rescue operations, resettlement support, and education.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <strong className="text-gray-900 text-lg">Pressure China to Stop Deportations</strong>
                    <p className="text-gray-700">Contact your representatives to demand China stop sending North Korean women back to torture and death. China is violating international refugee law.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <strong className="text-gray-900 text-lg">Amplify Survivor Voices</strong>
                    <p className="text-gray-700">Follow and share content from North Korean defector activists. Their testimonies are the only window into what's happening inside.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div>
                    <strong className="text-gray-900 text-lg">Educate Others</strong>
                    <p className="text-gray-700">Most people don't know the extent of women's suffering in North Korea. Share this information. Start conversations. Don't let it remain invisible.</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Women Still Inside</h2>

            <p className="text-lg text-gray-800 leading-relaxed mb-6">
              For every woman who escapes, millions remain. They wake up every day in a country that treats them as property, where their bodies are not their own, where their suffering is policy.
            </p>

            <p className="text-lg text-gray-800 leading-relaxed mb-6">
              <strong>They don't have social media. They can't call for help. They don't even know that help exists.</strong>
            </p>

            <p className="text-lg text-gray-800 leading-relaxed mb-6">
              So we must be their voice. We must refuse to look away. We must demand that the international community stop prioritizing politics over human lives.
            </p>

            <div className="bg-gray-900 text-white p-8 rounded-2xl my-12">
              <p className="text-2xl font-bold mb-4">
                "The world doesn't care about North Korean women because they're too far away, too foreign, too complicated."
              </p>
              <p className="text-xl">
                But they're not. They're daughters. Mothers. Sisters. They're us—born in the wrong place at the wrong time.
              </p>
              <p className="text-lg mt-4 text-gray-300">
                Their fight for freedom deserves the same attention as any other human rights crisis. They deserve to be seen.
              </p>
            </div>

            <div className="mt-12 pt-8 border-t-2 border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Organizations Working to Help North Korean Women:</h3>
              
              <div className="space-y-4">
                <div>
                  <strong className="text-gray-900">Liberty in North Korea (LiNK)</strong>
                  <p className="text-gray-700">Rescues North Korean refugees and helps them resettle in free countries.</p>
                  <a href="https://www.libertyinnorthkorea.org" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline">
                    www.libertyinnorthkorea.org
                  </a>
                </div>

                <div>
                  <strong className="text-gray-900">Teach North Korean Refugees (TNKR)</strong>
                  <p className="text-gray-700">Provides free English education to North Korean refugees in South Korea.</p>
                  <a href="https://www.teachnorthkoreanrefugees.org" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline">
                    www.teachnorthkoreanrefugees.org
                  </a>
                </div>

                <div>
                  <strong className="text-gray-900">Database Center for North Korean Human Rights (NKDB)</strong>
                  <p className="text-gray-700">Documents human rights violations and preserves survivor testimonies.</p>
                  <a href="https://www.nkdb.org" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline">
                    www.nkdb.org
                  </a>
                </div>

                <div>
                  <strong className="text-gray-900">Human Rights Watch - North Korea</strong>
                  <p className="text-gray-700">International advocacy and documentation of human rights abuses.</p>
                  <a href="https://www.hrw.org/asia/north-korea" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline">
                    www.hrw.org/asia/north-korea
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-16 p-8 bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl text-white text-center">
            <h3 className="text-3xl font-bold mb-4">Don't Let Their Voices Be Silenced</h3>
            <p className="text-xl mb-6 opacity-90">
              Share this article. Donate to organizations helping North Korean women. Demand political action. These women cannot speak for themselves—so we must speak for them.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => window.location.href = '/donate'}
                className="px-8 py-3 bg-white text-pink-600 font-semibold rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Support Women's Rights
              </button>
              <button
                onClick={() => window.location.href = '/blog'}
                className="px-8 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-pink-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Read More Articles
              </button>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
